import { useForm, SubmitHandler } from 'react-hook-form';
import { Input, Button, FormControl, FormLabel, Link, Box, useToast, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useDomainConfig } from '../../hooks/useDomainConfig';
import { useMutation, useQueryClient } from 'react-query';
import { signIn } from '../../apis/auth';
import { useUser } from '../../hooks/useUser';
import { AxiosError } from 'axios';
import { UIErrorResponse } from '../../../../../packages/types/src/error';
interface SignInFormInputs {
	email: string;
	password: string;
}

interface SignInFormProps {
	onSignUpClick?: () => void;
	onForgotPasswordClick?: () => void;
}

const SignInForm = ({ onSignUpClick, onForgotPasswordClick }: SignInFormProps) => {
	const toast = useToast();
	const { isLoading, isError, data } = useDomainConfig({ uiHost: window.location.host });
	const { refetch } = useUser();
	const { mutate: signInMutate, isLoading: isSignInLoading } = useMutation(signIn, {
		onSuccess: () => {
			refetch();
		},
		onError: (error: AxiosError<UIErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'User signin failed',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<SignInFormInputs>();
	if (isLoading) {
		return <div>Loading...</div>;
	}
	if (isError) {
		return <div>Error</div>;
	}

	const onSubmit: SubmitHandler<SignInFormInputs> = data => {
		// call sign in api
		// redirect to dashboard
		signInMutate(data);
	};
	const passwordPattern = /^(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
	return (
		<Box as="form" w="full" onSubmit={handleSubmit(onSubmit)}>
			<FormControl id="email" mb="24px" isInvalid={errors.email ? true : false}>
				<Text as={FormLabel} textStyle="body2-md">
					Email
				</Text>
				<Input type="email" placeholder="julie@example.com" {...register('email', { required: 'Email is required' })} />
				{errors.email && (
					<Text textStyle="body2" mt="6px" color="#D61E27">
						{errors.email.message}
					</Text>
				)}
			</FormControl>
			<FormControl id="password" mb="24px" isInvalid={errors.password ? true : false}>
				<Box display="flex" flexWrap="wrap" justifyContent="space-between">
					<Text as={FormLabel} textStyle="body2-md">
						Password
					</Text>

					<Link
						onClick={
							onForgotPasswordClick
								? e => {
										e.preventDefault();
										onForgotPasswordClick();
								  }
								: undefined
						}
						as={RouterLink}
						textStyle="body2-md"
						color="#386AFA"
						to="/forgot-password"
					>
						Forgot password?
					</Link>
				</Box>
				<Input placeholder="Enter your password" type="password" {...register('password', { required: 'Password is required' })} />
				{errors.password && (
					<Text textStyle="body2" mt="6px" color="#D61E27">
						{errors.password.message}
					</Text>
				)}
			</FormControl>

			<Box display={'flex'} gap="8px" alignItems={'center'}>
				<Button size="lg" textStyle="btn-lg" w="full" variant="primary" type="submit" isLoading={isSignInLoading}>
					Sign In
				</Button>
			</Box>

			<Box display="flex" gap="8px" color="#544D4F" textStyle="body1-md" justifyContent="center" textAlign="center" mt="40px" flexWrap="wrap">
				Don't have an account?
				<Link
					onClick={
						onSignUpClick
							? e => {
									e.preventDefault();
									onSignUpClick();
							  }
							: undefined
					}
					as={RouterLink}
					color="#386AFA"
					to="/sign-up"
				>
					Sign Up
				</Link>
			</Box>
		</Box>
	);
};

export default SignInForm;
